/**
 * 🗣️ SALONS THÉMATIQUES - Où les âmes se rencontrent
 * 
 * "Tu vas où ce soir ?" - Le salon des noctambules
 * "Les amoureux de la Terre" - Pour les écolos
 * "Club des philosophes" - Discussions profondes
 * 
 * Chaque salon a son ambiance, son énergie, sa tribu ! 💜
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MessageCircle, Users, Sparkles, Lock, Crown,
  Leaf, BookOpen, Film, Globe, Wine, Palette, Mountain, Zap, Sun,
  Heart, Star, Clock, Send, Smile
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { PASSION_CATEGORIES } from '../soul/SoulPassions';

// 🏠 SALONS THÉMATIQUES
export const THEMED_ROOMS = [
  // 🌙 Sorties & Soirées
  {
    id: 'tonight',
    name: '🍹 Tu sors où ce soir ?',
    description: 'Le QG des noctambules ! Partage tes plans, trouve des potes !',
    category: 'nightlife',
    emoji: '🍹',
    color: 'from-fuchsia-500 to-purple-600',
    bgImage: 'https://images.unsplash.com/photo-1566737236500-c8ac43014a67?w=400',
    maxUsers: 50,
    isHot: true,
    tags: ['bars', 'clubs', 'soirées', 'apéro'],
    welcomeMessage: 'Bienvenue dans le salon des fêtards ! 🎉 C\'est quoi le programme ce soir ?',
  },
  {
    id: 'bars_lens',
    name: '🍺 Bars du coin (Lens/Béthune)',
    description: 'Les meilleurs spots pour boire un verre dans le 62 !',
    category: 'nightlife',
    emoji: '🍺',
    color: 'from-amber-500 to-orange-600',
    maxUsers: 30,
    tags: ['bars', 'local', '62'],
    welcomeMessage: 'Salut la team 62 ! 🍻 Quel est votre bar préféré ?',
  },
  
  // 🌱 Nature & Terre
  {
    id: 'nature_lovers',
    name: '🌱 Les amoureux de la Terre',
    description: 'Permaculture, jardinage, balades... La nature nous unit !',
    category: 'nature',
    emoji: '🌱',
    color: 'from-green-500 to-emerald-600',
    bgImage: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=400',
    maxUsers: 40,
    isNew: true,
    tags: ['permaculture', 'jardinage', 'nature', 'écologie'],
    welcomeMessage: 'Bienvenue parmi les amoureux de la Terre 🌍 Qu\'est-ce qui pousse chez vous ?',
  },
  {
    id: 'hiking',
    name: '🥾 Rando & Montagne',
    description: 'Partagez vos sentiers, organisez des randos !',
    category: 'nature',
    emoji: '🥾',
    color: 'from-teal-500 to-cyan-600',
    maxUsers: 35,
    tags: ['randonnée', 'montagne', 'nature', 'aventure'],
    welcomeMessage: 'Salut les marcheurs ! 🏔️ C\'est quoi votre prochaine rando ?',
  },
  
  // 🧘 Spiritualité
  {
    id: 'positive_vibes',
    name: '✨ Positive Vibes Only',
    description: 'Pensée positive, loi d\'attraction, bienveillance...',
    category: 'spirituality',
    emoji: '✨',
    color: 'from-purple-500 to-violet-600',
    bgImage: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400',
    maxUsers: 40,
    isHot: true,
    tags: ['positif', 'spiritualité', 'bien-être', 'méditation'],
    welcomeMessage: 'Bienvenue dans le salon des bonnes ondes ! 🌈 Partagez votre gratitude du jour !',
  },
  {
    id: 'meditation',
    name: '🧘 Méditation & Zen',
    description: 'Un espace calme pour échanger sur la méditation...',
    category: 'spirituality',
    emoji: '🧘',
    color: 'from-indigo-500 to-purple-600',
    maxUsers: 25,
    tags: ['méditation', 'yoga', 'zen', 'calme'],
    welcomeMessage: 'Namaste 🙏 Prenez une grande respiration... Vous êtes au bon endroit.',
  },
  
  // 📚 Savoir & Réflexion
  {
    id: 'philosophers',
    name: '📚 Club des Philosophes',
    description: 'Discussions profondes, débats passionnés, refaire le monde...',
    category: 'knowledge',
    emoji: '📚',
    color: 'from-amber-500 to-orange-600',
    bgImage: 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=400',
    maxUsers: 30,
    tags: ['philosophie', 'débats', 'réflexion', 'culture'],
    welcomeMessage: 'Bienvenue penseurs ! 🤔 Aujourd\'hui on débat de quoi ?',
  },
  {
    id: 'book_club',
    name: '📖 Club de Lecture',
    description: 'Partagez vos lectures, recommandations, coups de cœur...',
    category: 'knowledge',
    emoji: '📖',
    color: 'from-rose-500 to-pink-600',
    maxUsers: 35,
    isNew: true,
    tags: ['lecture', 'livres', 'littérature'],
    welcomeMessage: 'Bienvenue au club ! 📚 Quel livre vous a marqué récemment ?',
  },
  
  // 🎬 Culture & Nostalgie
  {
    id: 'cinema_classic',
    name: '🎬 Cinéphiles Old School',
    description: 'Les classiques du cinéma, vieux films, nostalgie...',
    category: 'culture',
    emoji: '🎬',
    color: 'from-rose-500 to-pink-600',
    maxUsers: 30,
    tags: ['cinéma', 'films', 'classiques', 'nostalgie'],
    welcomeMessage: 'Bienvenue cinéphiles ! 🎥 C\'est quoi votre film culte ?',
  },
  {
    id: 'music_lovers',
    name: '🎵 Mélomanes',
    description: 'Partagez vos découvertes musicales, concerts, playlists...',
    category: 'culture',
    emoji: '🎵',
    color: 'from-violet-500 to-purple-600',
    maxUsers: 40,
    tags: ['musique', 'concerts', 'playlists'],
    welcomeMessage: 'Hey les mélomanes ! 🎶 Qu\'est-ce que vous écoutez en ce moment ?',
  },
  
  // 🌍 Engagement
  {
    id: 'eco_warriors',
    name: '🌍 Éco-Warriors',
    description: 'Écologie, zéro déchet, actions concrètes pour la planète !',
    category: 'engagement',
    emoji: '🌍',
    color: 'from-cyan-500 to-blue-600',
    maxUsers: 35,
    tags: ['écologie', 'zéro déchet', 'planète'],
    welcomeMessage: 'Bienvenue guerriers de la Terre ! 🌱 Quelle est votre action éco du jour ?',
  },
  
  // 🎨 Créativité
  {
    id: 'artists',
    name: '🎨 Âmes Créatives',
    description: 'Artistes, musiciens, écrivains... Partagez vos créations !',
    category: 'creativity',
    emoji: '🎨',
    color: 'from-indigo-500 to-blue-600',
    maxUsers: 35,
    tags: ['art', 'musique', 'écriture', 'création'],
    welcomeMessage: 'Bienvenue créateurs ! 🎨 Qu\'est-ce que vous créez en ce moment ?',
  },
  
  // 🏔️ Aventure
  {
    id: 'adventurers',
    name: '🏔️ Aventuriers',
    description: 'Voyages, road trips, découvertes... L\'appel de l\'aventure !',
    category: 'adventure',
    emoji: '🏔️',
    color: 'from-teal-500 to-cyan-600',
    maxUsers: 35,
    tags: ['voyage', 'aventure', 'road trip'],
    welcomeMessage: 'Salut les aventuriers ! ✈️ C\'est quoi votre prochaine destination ?',
  },
  
  // 🏃 Bien-être
  {
    id: 'fitness',
    name: '💪 Sportifs & Fit',
    description: 'Running, fitness, motivation... On se motive ensemble !',
    category: 'wellness',
    emoji: '💪',
    color: 'from-lime-500 to-green-600',
    maxUsers: 40,
    tags: ['sport', 'fitness', 'running', 'motivation'],
    welcomeMessage: 'Salut la team fit ! 🏃 Qui s\'est entraîné aujourd\'hui ?',
  },
  
  // ❤️ Amour & Relations
  {
    id: 'love_stories',
    name: '❤️ Histoires de Cœur',
    description: 'Conseils amoureux, partages, soutien... Entre nous !',
    category: 'love',
    emoji: '❤️',
    color: 'from-red-500 to-pink-600',
    maxUsers: 30,
    isHot: true,
    tags: ['amour', 'conseils', 'relations'],
    welcomeMessage: 'Bienvenue dans le salon du cœur ! 💕 Ici on s\'écoute et on se soutient.',
  },
];

// 🎨 Composant Carte de Salon
export const RoomCard = ({ 
  room, 
  onlineCount = 0, 
  onClick, 
  isDarkMode = true,
  isJoined = false,
}) => {
  const categoryInfo = PASSION_CATEGORIES.find(c => c.id === room.category);
  
  return (
    <motion.div
      whileHover={{ scale: 1.02, y: -2 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className={`relative overflow-hidden rounded-2xl cursor-pointer ${
        isDarkMode ? 'bg-gray-800' : 'bg-white'
      } shadow-lg`}
    >
      {/* Background gradient */}
      <div className={`absolute inset-0 bg-gradient-to-br ${room.color} opacity-20`} />
      
      {/* Background image if available */}
      {room.bgImage && (
        <div 
          className="absolute inset-0 opacity-10"
          style={{ 
            backgroundImage: `url(${room.bgImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
      )}
      
      {/* Content */}
      <div className="relative p-4">
        {/* Badges */}
        <div className="absolute top-2 right-2 flex gap-1">
          {/* 🎙️ Badge VOCAL - Tous les salons sont vocaux ! */}
          <span className="px-2 py-0.5 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold flex items-center gap-1">
            🎙️ VOCAL
          </span>
          {room.isHot && (
            <span className="px-2 py-0.5 rounded-full bg-red-500 text-white text-xs font-bold animate-pulse">
              🔥 HOT
            </span>
          )}
          {room.isNew && (
            <span className="px-2 py-0.5 rounded-full bg-green-500 text-white text-xs font-bold">
              ✨ NEW
            </span>
          )}
          {isJoined && (
            <span className="px-2 py-0.5 rounded-full bg-purple-500 text-white text-xs font-bold">
              ✓ Rejoint
            </span>
          )}
        </div>
        
        {/* Header */}
        <div className="flex items-start gap-3 mb-3">
          <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${room.color} flex items-center justify-center text-2xl shadow-lg`}>
            {room.emoji}
          </div>
          <div className="flex-1">
            <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {room.name}
            </h3>
            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {room.description}
            </p>
          </div>
        </div>
        
        {/* Footer */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span className={`text-xs ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                {onlineCount} en ligne
              </span>
            </div>
          </div>
          
          <div className="flex gap-1">
            {room.tags?.slice(0, 2).map((tag) => (
              <span 
                key={tag}
                className={`px-2 py-0.5 rounded-full text-xs ${
                  isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                }`}
              >
                #{tag}
              </span>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// 📋 Liste des salons par catégorie
export const RoomsList = ({ 
  onRoomSelect, 
  isDarkMode = true,
  userPassions = {},
  joinedRooms = [],
}) => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [onlineCounts, setOnlineCounts] = useState({});
  
  // Simuler les compteurs en ligne (à connecter au vrai backend)
  useEffect(() => {
    const counts = {};
    THEMED_ROOMS.forEach(room => {
      counts[room.id] = Math.floor(Math.random() * 20) + 5;
    });
    setOnlineCounts(counts);
  }, []);
  
  // Filtrer les salons
  const filteredRooms = activeFilter === 'all' 
    ? THEMED_ROOMS 
    : activeFilter === 'joined'
      ? THEMED_ROOMS.filter(r => joinedRooms.includes(r.id))
      : activeFilter === 'recommended'
        ? THEMED_ROOMS.filter(r => {
            const userCats = Object.keys(userPassions).filter(k => userPassions[k]?.length > 0);
            return userCats.includes(r.category);
          })
        : THEMED_ROOMS.filter(r => r.category === activeFilter);
  
  // Trier : HOT d'abord, puis par nombre en ligne
  const sortedRooms = [...filteredRooms].sort((a, b) => {
    if (a.isHot && !b.isHot) return -1;
    if (!a.isHot && b.isHot) return 1;
    return (onlineCounts[b.id] || 0) - (onlineCounts[a.id] || 0);
  });
  
  const filters = [
    { id: 'all', name: '🌐 Tous', count: THEMED_ROOMS.length },
    { id: 'recommended', name: '✨ Pour toi', count: null },
    { id: 'joined', name: '💜 Mes salons', count: joinedRooms.length },
    { id: 'nightlife', name: '🍹 Sorties', count: THEMED_ROOMS.filter(r => r.category === 'nightlife').length },
    { id: 'nature', name: '🌱 Nature', count: THEMED_ROOMS.filter(r => r.category === 'nature').length },
    { id: 'spirituality', name: '🧘 Éveil', count: THEMED_ROOMS.filter(r => r.category === 'spirituality').length },
    { id: 'knowledge', name: '📚 Savoir', count: THEMED_ROOMS.filter(r => r.category === 'knowledge').length },
  ];
  
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="text-center">
        <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          🗣️ Salons Thématiques
        </h3>
        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Rejoins ta tribu et rencontre des âmes qui vibrent comme toi ! ✨
        </p>
      </div>
      
      {/* Filtres - Scrollable horizontalement */}
      <div className="overflow-x-auto pb-2 -mx-2 px-2">
        <div className="flex gap-2 min-w-max">
          {filters.map((filter) => (
            <button
              key={filter.id}
              onClick={() => setActiveFilter(filter.id)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                activeFilter === filter.id
                  ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg'
                  : isDarkMode
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {filter.name}
              {filter.count !== null && (
                <span className="ml-1 opacity-70">({filter.count})</span>
              )}
            </button>
          ))}
        </div>
      </div>
      
      {/* Liste des salons */}
      <div className="grid gap-3">
        {sortedRooms.length === 0 ? (
          <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            <p>Aucun salon trouvé 🔍</p>
            <p className="text-sm">Essaie un autre filtre !</p>
          </div>
        ) : (
          sortedRooms.map((room) => (
            <RoomCard
              key={room.id}
              room={room}
              onlineCount={onlineCounts[room.id] || 0}
              onClick={() => onRoomSelect(room)}
              isDarkMode={isDarkMode}
              isJoined={joinedRooms.includes(room.id)}
            />
          ))
        )}
      </div>
    </div>
  );
};

// 💬 Vue du salon (chat)
export const RoomChatView = ({
  room,
  messages = [],
  onSendMessage,
  onClose,
  currentUser,
  isDarkMode = true,
}) => {
  const [newMessage, setNewMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  
  const handleSend = () => {
    if (!newMessage.trim()) return;
    onSendMessage(newMessage.trim());
    setNewMessage('');
  };
  
  return (
    <div className={`flex flex-col h-full ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <div className={`p-4 bg-gradient-to-r ${room.color} text-white`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{room.emoji}</span>
            <div>
              <h3 className="font-bold">{room.name}</h3>
              <p className="text-xs text-white/80">{room.description}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-white hover:bg-white/20"
          >
            ✕
          </Button>
        </div>
      </div>
      
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {/* Welcome message */}
        <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-purple-500/20' : 'bg-purple-100'} text-center`}>
          <p className={`text-sm ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>
            {room.welcomeMessage}
          </p>
        </div>
        
        {/* Messages */}
        {messages.map((msg, idx) => {
          const isMe = msg.userId === currentUser?.id;
          
          return (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`max-w-[80%] ${isMe ? 'order-2' : 'order-1'}`}>
                {!isMe && (
                  <p className={`text-xs mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {msg.userName}
                  </p>
                )}
                <div className={`px-4 py-2 rounded-2xl ${
                  isMe
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                    : isDarkMode
                      ? 'bg-gray-800 text-gray-200'
                      : 'bg-white text-gray-800'
                }`}>
                  <p className="text-sm">{msg.text}</p>
                </div>
                <p className={`text-xs mt-1 ${isMe ? 'text-right' : 'text-left'} ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {msg.time}
                </p>
              </div>
            </motion.div>
          );
        })}
        
        {isTyping && (
          <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Quelqu'un écrit...
          </div>
        )}
      </div>
      
      {/* Input */}
      <div className={`p-4 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="flex gap-2">
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSend()}
            placeholder="Écris ton message..."
            className={isDarkMode ? 'bg-gray-800 border-gray-700' : ''}
          />
          <Button
            onClick={handleSend}
            disabled={!newMessage.trim()}
            className="bg-gradient-to-r from-purple-500 to-pink-500 text-white"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RoomsList;
